import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Toaster } from '@/components/ui/sonner';
import { ShieldCheck, ArrowLeft, Info, CheckCircle2 } from 'lucide-react';

const SECTORS = [
  'Hydropower / Dams',
  'Transmission networks',
  'Pipelines (oil & gas)',
  'Mining & Tailings',
  'Railways / Highways',
  'Ports & Coastal Assets',
  'Water resources',
  'Disaster management',
  'Other',
];

export default function RequestDemoPage() {
  const nav = useNavigate();
  const [f, setF] = useState({
    name: '',
    designation: '',
    organization: '',
    department: '',
    email: '',
    mobile: '',
    sector: '',
    assets_count: '',
    primary_requirement: '',
    preferred_date: '',
    message: '',
    country: 'India',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/public/demo-request', f);
      setDone(true);
      toast.success('Request received. Our team will reach out within one business day.');
    } catch (err) {
      toast.error('We could not submit your request. Please try again or contact us directly.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 py-16 lg:py-20">
      <Toaster />
      <button
        onClick={() => nav('/')}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
        data-testid="demo-back-link"
      >
        <ArrowLeft size={14} /> Back to homepage
      </button>

      <div className="mt-4 max-w-2xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
          <ShieldCheck size={12} /> Executive demonstration
        </div>
        <h1 className="font-display text-3xl md:text-4xl lg:text-[42px] font-semibold mt-4 leading-tight">
          Request an executive demonstration.
        </h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          Share a few details about your organisation and infrastructure portfolio, and our team will schedule a tailored session that reflects your operational context.
        </p>
      </div>

      {done ? (
        <Card className="mt-10 p-10 border-border text-center" data-testid="demo-request-success">
          <CheckCircle2 size={44} className="text-primary mx-auto" />
          <div className="mt-4 font-display text-2xl font-semibold">Thank you — request received.</div>
          <div className="mt-3 text-muted-foreground max-w-md mx-auto">
            Our solutions team will reach out within one business day to coordinate a suitable date and prepare a demonstration environment aligned to your sector.
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            This acknowledgement does not confirm a scheduled meeting.
          </div>
          <div className="mt-6">
            <Button variant="outline" onClick={() => nav('/')} data-testid="demo-return-home">
              Return to homepage
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="mt-10 p-6 lg:p-8 border-border">
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="demo-request-form">
            <div>
              <Label htmlFor="demo-name">Name <span className="text-red-500">*</span></Label>
              <Input id="demo-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="demo-name" />
            </div>
            <div>
              <Label htmlFor="demo-designation">Designation</Label>
              <Input id="demo-designation" value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} placeholder="e.g., Executive Director" data-testid="demo-designation" />
            </div>
            <div>
              <Label htmlFor="demo-org">Organisation <span className="text-red-500">*</span></Label>
              <Input id="demo-org" required value={f.organization} onChange={(e) => setF({ ...f, organization: e.target.value })} placeholder="e.g., THDC India Ltd" data-testid="demo-org" />
            </div>
            <div>
              <Label htmlFor="demo-department">Department</Label>
              <Input id="demo-department" value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} placeholder="e.g., Dam Safety / O&M" data-testid="demo-department" />
            </div>
            <div>
              <Label htmlFor="demo-email">Official email <span className="text-red-500">*</span></Label>
              <Input id="demo-email" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="demo-email" />
            </div>
            <div>
              <Label htmlFor="demo-mobile">Mobile number</Label>
              <Input id="demo-mobile" type="tel" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} placeholder="+91-" data-testid="demo-mobile" />
            </div>
            <div>
              <Label htmlFor="demo-sector">Infrastructure sector</Label>
              <Select value={f.sector} onValueChange={(v) => setF({ ...f, sector: v })}>
                <SelectTrigger id="demo-sector" data-testid="demo-sector">
                  <SelectValue placeholder="Select a sector" />
                </SelectTrigger>
                <SelectContent>
                  {SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="demo-assets">Number / type of assets</Label>
              <Input id="demo-assets" value={f.assets_count} onChange={(e) => setF({ ...f, assets_count: e.target.value })} placeholder="e.g., 3 hydro projects, 500 km transmission corridor" data-testid="demo-assets" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="demo-req">Primary monitoring requirement</Label>
              <Input id="demo-req" value={f.primary_requirement} onChange={(e) => setF({ ...f, primary_requirement: e.target.value })} placeholder="e.g., Landslide monitoring around reservoir slopes" data-testid="demo-requirement" />
            </div>
            <div>
              <Label htmlFor="demo-date">Preferred demonstration date</Label>
              <Input id="demo-date" type="date" value={f.preferred_date} onChange={(e) => setF({ ...f, preferred_date: e.target.value })} data-testid="demo-date" />
            </div>
            <div>
              <Label htmlFor="demo-country">Country</Label>
              <Input id="demo-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} data-testid="demo-country" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="demo-message">Message</Label>
              <Textarea id="demo-message" rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Any specific concerns, context, or questions you would like the demonstration to address." data-testid="demo-message" />
            </div>
            <div className="md:col-span-2 flex items-start gap-2 text-xs text-muted-foreground">
              <Info size={12} className="mt-0.5 shrink-0" />
              <span>
                Fields marked <span className="text-red-500">*</span> are mandatory. We use this information only to prepare a relevant demonstration and to contact you — nothing else.
              </span>
            </div>
            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" size="lg" disabled={busy} data-testid="demo-submit">
                {busy ? 'Submitting…' : 'Submit request'}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
